import { useState, useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { detectLocale, LOCALE_CONFIG, formatCurrency, DEFAULT_LOCALE } from "../locales/config";
import { getPdfStrings } from "../locales/pdfStrings";
import { getUiStrings } from "../locales/uiStrings";
import { CookieBanner } from "./CookieBanner";
import { localePath } from "../utils/localePath";
import { getPriceForLocale } from "../locales/pricing";

const COLORS = {
  bg: "#0a0a0f", surface: "#13131a", border: "#1e1e2e",
  accent: "#00e5a0", accentDim: "#00e5a015", accentMid: "#00e5a055",
  text: "#e8e8f0", textMuted: "#6b6b8a", red: "#ff4d6d",
  yellow: "#ffd166", white: "#ffffff", blue: "#4d9fff", purple: "#a78bfa",
};

const DEBT_TYPES = [
  { label: "Select type...", value: "" },
  { label: "Credit Card", value: "Credit Card" },
  { label: "Store / Retail Card", value: "Store Card" },
  { label: "Personal Loan", value: "Personal Loan" },
  { label: "Car Loan", value: "Car Loan" },
  { label: "Student Loan", value: "Student Loan" },
  { label: "Medical Debt", value: "Medical Debt" },
  { label: "Home Equity Loan", value: "Home Equity Loan" },
  { label: "Payday Loan", value: "Payday Loan" },
  { label: "Buy Now Pay Later", value: "Buy Now Pay Later" },
  { label: "Family / Friend Loan", value: "Family Loan" },
  { label: "Other", value: "Other" },
];

const fmt = v => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
const fmtMonth = m => { const y = Math.floor(m / 12), mo = m % 12; if (!y) return `${m} months`; if (!mo) return `${y}yr`; return `${y}yr ${mo}mo`; };

function calcPayoff(debts, extra, method) {
  let r = debts
    .map(d => ({ ...d, bal: parseFloat(d.balance) || 0, rate: (parseFloat(d.rate) || 0) / 100 / 12, minP: parseFloat(d.minPayment) || 0 }))
    .filter(d => d.bal > 0);
  if (!r.length) return { months: 0, totalInterest: 0, schedule: [], timedOut: false };
  if (method === "avalanche") r.sort((a, b) => b.rate - a.rate);
  else if (method === "snowball") r.sort((a, b) => a.bal - b.bal);
  else if (method === "highbal") r.sort((a, b) => b.bal - a.bal);
  else if (method === "lowint") r.sort((a, b) => a.rate - b.rate);
  let months = 0, totalInterest = 0, schedule = [];
  while (r.length > 0 && months < 600) {
    months++;
    let mi = 0;
    r = r.map(d => { const i = d.bal * d.rate; mi += i; return { ...d, bal: d.bal + i }; });
    totalInterest += mi;
    r = r.map(d => ({ ...d, bal: Math.max(0, d.bal - d.minP) }));
    if (r.length > 0) { const e = Math.min(extra, r[0].bal); r[0] = { ...r[0], bal: r[0].bal - e }; }
    const paid = r.filter(d => d.bal <= 0.01);
    if (paid.length) { const rolled = paid.reduce((s, d) => s + d.minP, 0); r = r.filter(d => d.bal > 0.01); if (r.length) r[0] = { ...r[0], minP: r[0].minP + rolled }; }
    schedule.push({ month: months, balance: r.reduce((s, d) => s + d.bal, 0), interest: mi });
  }
  return { months, totalInterest, schedule, timedOut: months >= 600 };
}

const emptyDebt = () => ({ id: Date.now() + Math.random(), type: "", balance: "", rate: "", minPayment: "", errors: {} });

const Chart = ({ schedule, color }) => {
  if (!schedule?.length) return null;
  const max = schedule[0].balance + (schedule[0].interest || 0);
  const f = schedule.filter((_, i) => i % Math.max(1, Math.floor(schedule.length / 50)) === 0);
  const pts = f.map((r, i, a) => `${(i / Math.max(a.length - 1, 1)) * 100},${100 - (r.balance / max) * 88 - 6}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", overflow: "visible" }} preserveAspectRatio="none">
      <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.3" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <polygon points={`0,100 ${pts} 100,100`} fill="url(#cg)" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
};

function validateDebts(debts, u, fmtL) {
  let hasError = false;
  const validated = debts.map(d => {
    const errors = {};
    if (!d.type) errors.type = u.required;
    if (!d.balance || parseFloat(d.balance) <= 0) errors.balance = u.required;
    if (d.rate === "" || d.rate === undefined) errors.rate = u.required;
    if (!d.minPayment || parseFloat(d.minPayment) <= 0) errors.minPayment = u.required;
    // Check if min payment covers interest
    const bal = parseFloat(d.balance) || 0;
    const rate = (parseFloat(d.rate) || 0) / 100 / 12;
    const min = parseFloat(d.minPayment) || 0;
    if (bal > 0 && rate > 0 && min > 0 && min <= bal * rate) {
      errors.minPayment = u.minPaymentError(fmtL(bal * rate));
    }
    if (Object.keys(errors).length > 0) hasError = true;
    return { ...d, errors };
  });
  return { validated, hasError };
}

export default function Calculator({ defaultDebtType = null, lockedDebtType = false, defaultMethod = "avalanche", locale: localeProp = null, showHelmet = true, showHeader = true, beforeFooter = null } = {}) {
  const [debts, setDebts] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const isReturning = params.get("paid") === "true" || window.location.hash === "#results";
    const saved = isReturning ? localStorage.getItem("clearmydebt_debts") : null;
    if (saved) return JSON.parse(saved);
    if (defaultDebtType) {
      return [{ id: 1, type: defaultDebtType, balance: "", rate: "", minPayment: "", errors: {} }];
    }
    const initCfg = LOCALE_CONFIG[localeProp] || LOCALE_CONFIG[DEFAULT_LOCALE];
    const initTypes = initCfg.debtTypes;
    return [
      { id: 1, type: initTypes[0] || "Credit Card", balance: "8500", rate: "22.99", minPayment: "200", errors: {} },
      { id: 2, type: initTypes[2] || initTypes[1] || "Car Loan", balance: "12000", rate: "7.5", minPayment: "350", errors: {} },
      { id: 3, type: initTypes[1] || "Personal Loan", balance: "5000", rate: "14.99", minPayment: "150", errors: {} },
    ];
  });
  const [extraScenarios, setExtraScenarios] = useState(["100", "200", "300"]);
  const extra = extraScenarios[1];
  const [method, setMethod] = useState(defaultMethod);
  const [step, setStep] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "true") return "result";
    if (window.location.hash === "#results" && localStorage.getItem("clearmydebt_results")) return "result";
    return "input";
  });
  const [results, setResults] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const isReturning = params.get("paid") === "true" || window.location.hash === "#results";
    const saved = isReturning ? localStorage.getItem("clearmydebt_results") : null;
    return saved ? JSON.parse(saved) : null;
  });
  const [recommendation, setRecommendation] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const isReturning = params.get("paid") === "true" || window.location.hash === "#results";
    const saved = isReturning ? localStorage.getItem("clearmydebt_rec") : null;
    return saved ? JSON.parse(saved) : null;
  }); // { bestMethod, sweetSpotExtra, interestSaved }
  const [generating, setGenerating] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [jspdfReady, setJspdfReady] = useState(!!window.jspdf);
  const [isPaid, setIsPaid] = useState(() => new URLSearchParams(window.location.search).get("paid") === "true");
  const [locale, setLocale] = useState(localeProp || DEFAULT_LOCALE);
  const isDemoRef = useRef(true);
  useEffect(() => {
    if (!localeProp) {
      detectLocale().then(detected => {
        setLocale(detected);
        if (isDemoRef.current) {
          const cfg = LOCALE_CONFIG[detected] || LOCALE_CONFIG[DEFAULT_LOCALE];
          const types = cfg.debtTypes;
          setDebts(prev => prev.map((d, i) => ({ ...d, type: types[i] || types[0] || d.type })));
        }
      });
    }
  }, [localeProp]);
  const firstErrorRef = useRef(null);

  const localeCfg = LOCALE_CONFIG[locale] || LOCALE_CONFIG[DEFAULT_LOCALE];
  const fmtL = v => formatCurrency(v, locale);
  const u = getUiStrings(locale);
  const regionalPrice = getPriceForLocale(locale);
  const localeDebtTypes = [
    { label: u.selectType, value: "" },
    ...localeCfg.debtTypes.map(t => ({ label: t, value: t })),
  ];

  useEffect(() => {
    if (!window.jspdf) {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = () => setJspdfReady(true);
      document.head.appendChild(s);
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "true") {
      window.history.replaceState({}, "", "/#results");
      localStorage.removeItem("clearmydebt_debts");
      localStorage.removeItem("clearmydebt_results");
      localStorage.removeItem("clearmydebt_rec");
      return;
    }

    // Hash-based navigation: /#results → verify data exists, else redirect to /
    if (window.location.hash === "#results") {
      if (!localStorage.getItem("clearmydebt_results")) {
        window.history.replaceState({}, "", "/");
      }
    }

    // Browser back/forward button support
    const onPopState = () => {
      if (window.location.hash !== "#results") setStep("input");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleBuyPDF = async () => {
    try {
      localStorage.setItem("clearmydebt_debts", JSON.stringify(debts));
      localStorage.setItem("clearmydebt_results", JSON.stringify(results));
      if (recommendation) localStorage.setItem("clearmydebt_rec", JSON.stringify(recommendation));
      const res = await fetch("/.netlify/functions/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale }),
      });
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      alert(u.paymentUnavailable);
    }
  };

  const totalDebt = debts.reduce((s, d) => s + (parseFloat(d.balance) || 0), 0);
  const active = results ? results[method] : null;

  const METHODS = [
    { key: "avalanche", label: u.methodAvalanche, desc: u.methodAvalancheDesc, color: COLORS.accent },
    { key: "snowball", label: u.methodSnowball, desc: u.methodSnowballDesc, color: COLORS.yellow },
  ];
  const ALL_METHODS = [
    { key: "avalanche", label: u.methodAvalanche, desc: u.methodAvalancheDesc, color: COLORS.accent },
    { key: "snowball", label: u.methodSnowball, desc: u.methodSnowballDesc, color: COLORS.yellow },
    { key: "highbal", label: u.methodHighbal, desc: u.methodHighbalDesc, color: COLORS.blue },
    { key: "lowint", label: u.methodLowint, desc: u.methodLowintDesc, color: COLORS.purple },
  ];

  const calculate = useCallback(() => {
    const { validated, hasError } = validateDebts(debts, u, fmtL);
    setDebts(validated);
    if (hasError) {
      // Focus first error field
      setTimeout(() => { if (firstErrorRef.current) firstErrorRef.current.focus(); }, 50);
      return;
    }
    const res = {};
    ["avalanche", "snowball", "highbal", "lowint"].forEach(key => { res[key] = calcPayoff(debts, parseFloat(extra) || 0, key); });
    setResults(res);

    // Compute best method + sweet spot for badge and PDF consistency
    const scenExtras = extraScenarios.map(e => parseFloat(e) || 0);
    const bestKey = ["avalanche","snowball","highbal","lowint"]
      .reduce((best, key) => {
        const tot = scenExtras.reduce((s, e) => s + calcPayoff(debts, e, key).totalInterest, 0);
        const bTot = scenExtras.reduce((s, e) => s + calcPayoff(debts, e, best).totalInterest, 0);
        return tot < bTot ? key : best;
      }, "avalanche");
    // Sweet spot = best among the 3 user-entered values (never an invented number)
    const sortedExtras = [...scenExtras].sort((a, b) => a - b);
    let ssPrevInt = calcPayoff(debts, 0, bestKey).totalInterest;
    let ssFirstMarginal = null, sweetSpot = sortedExtras[0];
    for (let i = 0; i < sortedExtras.length; i++) {
      const e = sortedExtras[i];
      const stepSize = e - (i === 0 ? 0 : sortedExtras[i - 1]);
      if (stepSize <= 0) continue;
      const ssInt = calcPayoff(debts, e, bestKey).totalInterest;
      const m = (ssPrevInt - ssInt) / stepSize * 100;
      if (ssFirstMarginal === null) ssFirstMarginal = m;
      if (m < ssFirstMarginal * 0.30) break;
      sweetSpot = e; ssPrevInt = ssInt;
    }
    const baselineInt = calcPayoff(debts, 0, bestKey).totalInterest;
    const bestInt = calcPayoff(debts, sweetSpot, bestKey).totalInterest;
    setRecommendation({ bestMethod: bestKey, sweetSpotExtra: sweetSpot, interestSaved: Math.max(0, baselineInt - bestInt) });

    const recValue = { bestMethod: bestKey, sweetSpotExtra: sweetSpot, interestSaved: Math.max(0, baselineInt - bestInt) };
    localStorage.setItem("clearmydebt_debts", JSON.stringify(debts));
    localStorage.setItem("clearmydebt_results", JSON.stringify(res));
    localStorage.setItem("clearmydebt_rec", JSON.stringify(recValue));
    window.history.pushState({}, "", window.location.pathname + "#results");
    setStep("result");
    setDownloaded(false);
  }, [debts, extraScenarios]);

  const updateDebt = (id, field, value) => {
    isDemoRef.current = false;
    setDebts(ds => ds.map(d => d.id === id ? { ...d, [field]: value, errors: { ...d.errors, [field]: undefined } } : d));
  };

  const generatePDF = async () => {
    if (!jspdfReady || !results) { alert(u.pdfLoading); return; }
    setGenerating(true);
    try {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const t = getPdfStrings(locale);
      const W = 210, M = 16, CW = W - M * 2;
      // jsPDF's built-in Helvetica has no metrics for Latin Extended characters.
      // Strip diacritics globally so splitTextToSize measures widths correctly and
      // text doesn't overflow the page on Eastern-European locales (cs, sk, pl…).
      const pdfText = (str) => typeof str === "string"
        ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[łŁ]/g, l => l === "ł" ? "l" : "L")
        : str;
      // Patch doc.text and doc.splitTextToSize to auto-normalize every string,
      // so no individual call site needs to remember to call pdfText().
      const _origText = doc.text.bind(doc);
      const _origSplit = doc.splitTextToSize.bind(doc);
      const _norm = (s) => Array.isArray(s) ? s.map(pdfText) : pdfText(s);
      doc.text = (text, ...args) => _origText(_norm(text), ...args);
      doc.splitTextToSize = (text, maxW, opts) => _origSplit(pdfText(text), maxW, opts);
      const now = new Date();
      const MONTHS = t.months;
      const minTot = debts.reduce((s, d) => s + (parseFloat(d.minPayment) || 0), 0);
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

      // Auto-select best method across all 3 extra payment scenarios — ignores UI toggle
      const allMethodKeys = ["avalanche", "snowball", "highbal", "lowint"];
      const scenarioExtras = extraScenarios.map(e => parseFloat(e) || 0);
      const methodScenarioTotals = {};
      allMethodKeys.forEach(key => {
        methodScenarioTotals[key] = scenarioExtras.reduce((sum, e) => sum + calcPayoff(debts, e, key).totalInterest, 0);
      });
      const method = allMethodKeys.reduce((best, key) => methodScenarioTotals[key] < methodScenarioTotals[best] ? key : best, "avalanche");
      const mLabel = t.methodLabels[method];
      const mDesc = t.methodDescs[method];
      const worstMethodKey = allMethodKeys.reduce((worst, key) => methodScenarioTotals[key] > methodScenarioTotals[worst] ? key : worst, "avalanche");
      const worstMethodLabel = t.methodLabels[worstMethodKey];

      // Sweet spot = best among the 3 user-entered values (never an invented number)
      const sortedScenExtras = [...scenarioExtras].sort((a, b) => a - b);
      let ssPrevInt = calcPayoff(debts, 0, method).totalInterest;
      let ssFirstMarginal = null;
      let sweetSpotExtra = sortedScenExtras[0];
      for (let i = 0; i < sortedScenExtras.length; i++) {
        const e = sortedScenExtras[i];
        const stepSize = e - (i === 0 ? 0 : sortedScenExtras[i - 1]);
        if (stepSize <= 0) continue;
        const ssInt = calcPayoff(debts, e, method).totalInterest;
        const marginalPer100 = (ssPrevInt - ssInt) / stepSize * 100;
        if (ssFirstMarginal === null) ssFirstMarginal = marginalPer100;
        if (marginalPer100 < ssFirstMarginal * 0.30) break;
        sweetSpotExtra = e;
        ssPrevInt = ssInt;
      }

      // Build user scenario data (Conservative, Balanced, Aggressive)
      const userScenarios = [
        { label: t.conservative, extra: scenarioExtras[0] },
        { label: t.balanced,     extra: scenarioExtras[1] },
        { label: t.aggressive,   extra: scenarioExtras[2] },
      ].map(s => ({ ...s, r: calcPayoff(debts, s.extra, method) }));
      const baselineInt = calcPayoff(debts, 0, method).totalInterest;
      userScenarios.forEach((s, i) => {
        s.intSaved   = baselineInt - s.r.totalInterest;
        s.marginal   = i === 0 ? s.intSaved : userScenarios[i-1].r.totalInterest - s.r.totalInterest;
        s.isSweetSpot = s.extra === sweetSpotExtra;
      });
      // Closest scenario to sweet spot = recommended
      const recScenario = userScenarios.reduce((best, s) =>
        Math.abs(s.extra - sweetSpotExtra) < Math.abs(best.extra - sweetSpotExtra) ? s : best
      , userScenarios[0]);

      // Use sweet spot extra as the basis for the full report
      const extraVal = sweetSpotExtra || scenarioExtras[1];
      const act = calcPayoff(debts, extraVal, method);
      // Savings vs worst method — at sweet spot extra (one real comparable number)
      const methodSavingsVsWorst = Math.max(0, Math.round(calcPayoff(debts, extraVal, worstMethodKey).totalInterest - act.totalInterest));

      const BG = [10, 10, 15];
      const SURF = [19, 19, 26];
      const ACC = [0, 229, 160];
      const MUTED = [107, 107, 138];
      const TEXT = [220, 220, 235];
      const RED = [255, 77, 109];
      const YEL = [255, 209, 102];
      const BLUE = [77, 159, 255];
      const PURP = [167, 139, 250];
      const TOTAL = 7;

      const pageHeader = (pageNum, title) => {
        doc.setFillColor(...BG); doc.rect(0, 0, W, 297, "F");
        doc.setFillColor(...ACC); doc.rect(0, 0, W, 2, "F");
        doc.setFillColor(...SURF); doc.rect(0, 2, W, 20, "F");
        doc.setTextColor(...ACC); doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text("ClearMyDebt", M, 11);
        doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(`Page ${pageNum} of ${TOTAL}  |  Generated ${MONTHS[now.getMonth()]} ${now.getFullYear()}  |  ${t.preparedBy}`, W - M, 11, { align: "right" });
        doc.setTextColor(...TEXT); doc.setFontSize(13); doc.setFont("helvetica", "bold");
        doc.text(title, M, 28);
      };

      const pageFooter = () => {
        doc.setDrawColor(...SURF); doc.setLineWidth(0.3); doc.line(M, 285, W - M, 285);
        doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(t.disclaimer, W / 2, 290, { align: "center" });
      };

      const methodDefs = [
        { key: "avalanche", label: t.methodLabels.avalanche, desc: t.methodDescsP2.avalanche, c: ACC },
        { key: "snowball",  label: t.methodLabels.snowball,  desc: t.methodDescsP2.snowball,  c: YEL },
        { key: "highbal",   label: t.methodLabels.highbal,   desc: t.methodDescsP2.highbal,   c: BLUE },
        { key: "lowint",    label: t.methodLabels.lowint,    desc: t.methodDescsP2.lowint,    c: PURP },
      ];

      // ── PAGE 1 CALCULATIONS ──
      const activDebtsP1 = debts.filter(d => parseFloat(d.balance) > 0);
      const weightedAvgRate = totalDebt > 0
        ? activDebtsP1.reduce((s, d) => s + (parseFloat(d.balance) || 0) * (parseFloat(d.rate) || 0), 0) / totalDebt
        : 0;
      const zeroExtraResult = calcPayoff(debts, 0, method);
      const zeroExtraMonths = zeroExtraResult.months;
      const zeroExtraTotalInterest = zeroExtraResult.totalInterest;
      const timeSavedMonths = Math.max(0, zeroExtraMonths - act.months);
      const interestSaved = Math.max(0, zeroExtraTotalInterest - act.totalInterest);
      const FULL_MONTHS = t.monthsFull;
      const currentMonthYear = `${FULL_MONTHS[now.getMonth()]} ${now.getFullYear()}`;
      const debtFreeDateObj = new Date(now.getFullYear(), now.getMonth() + act.months, 1);
      const zeroDebtFreeDateObj = new Date(now.getFullYear(), now.getMonth() + zeroExtraMonths, 1);
      const zeroExtraDebtFreeDate = `${MONTHS[zeroDebtFreeDateObj.getMonth()]} ${zeroDebtFreeDateObj.getFullYear()}`;
      const timeSavedYrs = Math.floor(timeSavedMonths / 12);
      const timeSavedMos = timeSavedMonths % 12;
      const timeSavedStr = timeSavedYrs > 0 && timeSavedMos > 0 ? `${timeSavedYrs}yr ${timeSavedMos}mo`
        : timeSavedYrs > 0 ? `${timeSavedYrs}yr` : `${timeSavedMos}mo`;

      // ── PAGE 1: YOUR DEBT SNAPSHOT ──
      pageHeader(1, t.p1Title);
      let y = 36;
      doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.text(`${t.whereYouStand}  -  ${currentMonthYear.toUpperCase()}`, M, y);
      y += 10;

      // Hero box
      doc.setFillColor(13, 17, 23); doc.roundedRect(M, y, CW, 28, 3, 3, "F");
      doc.setTextColor(107, 107, 138); doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.text(t.totalDebt, W / 2, y + 7, { align: "center" });
      doc.setTextColor(255, 255, 255); doc.setFontSize(28); doc.setFont("helvetica", "bold");
      doc.text(fmtL(totalDebt), W / 2, y + 20, { align: "center" });
      doc.setTextColor(107, 107, 138); doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.text(t.acrossAccounts(activDebtsP1.length), W / 2, y + 26, { align: "center" });
      y += 36;

      // 3-column stats
      const statW = (CW - 8) / 3;
      [
        { v: `${fmtL(minTot)}/mo`, l: t.minPaymentPerMo, dark: false },
        { v: `${weightedAvgRate.toFixed(1)}%`, l: t.avgInterestRate, dark: true },
        { v: t.fmtDuration(zeroExtraMonths), l: t.payingMinimumsOnly, dark: true },
      ].forEach((st, i) => {
        const x = M + i * (statW + 4);
        doc.setFillColor(...SURF); doc.roundedRect(x, y, statW, 22, 2, 2, "F");
        doc.setDrawColor(35, 35, 50); doc.setLineWidth(0.3); doc.roundedRect(x, y, statW, 22, 2, 2, "S");
        doc.setTextColor(st.dark ? RED[0] : TEXT[0], st.dark ? RED[1] : TEXT[1], st.dark ? RED[2] : TEXT[2]);
        doc.setFontSize(11); doc.setFont("helvetica", "bold");
        doc.text(st.v, x + statW / 2, y + 10, { align: "center" });
        doc.setTextColor(107, 107, 138); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(st.l, x + statW / 2, y + 17, { align: "center" });
      });
      y += 30;

      // Warning box
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      const warnL1 = doc.splitTextToSize(t.minimumsWarning(fmtL(zeroExtraTotalInterest), zeroExtraDebtFreeDate), CW - 8);
      const warnL2 = doc.splitTextToSize(t.withExtraSavings(fmtL(extraVal), fmtL(interestSaved), t.fmtDuration(timeSavedMonths)), CW - 8);
      const warnLH = 4.5;
      const warnBoxH = 6 + warnL1.length * warnLH + 3 + warnL2.length * warnLH + 4;
      doc.setFillColor(35, 25, 0); doc.roundedRect(M, y, CW, warnBoxH, 2, 2, "F");
      doc.setDrawColor(245, 158, 11); doc.setLineWidth(0.4); doc.roundedRect(M, y, CW, warnBoxH, 2, 2, "S");
      doc.setTextColor(...YEL);
      doc.text(warnL1, M + 4, y + 9);
      doc.text(warnL2, M + 4, y + 9 + warnL1.length * warnLH + 3);
      y += warnBoxH + 8;

      // Recommended strategy box
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      const stratDescLines = doc.splitTextToSize(t.methodSavingsDesc(mDesc, fmtL(methodSavingsVsWorst), worstMethodLabel, fmtL(extraVal)), CW - 34);
      const stratBoxH = Math.max(22, 18 + stratDescLines.length * 4);
      doc.setFillColor(0, 35, 20); doc.roundedRect(M, y, CW, stratBoxH, 2, 2, "F");
      doc.setDrawColor(...ACC); doc.setLineWidth(0.4); doc.roundedRect(M, y, CW, stratBoxH, 2, 2, "S");
      doc.setFillColor(...ACC); doc.roundedRect(M + 4, y + 5, 26, 5.5, 1, 1, "F");
      doc.setTextColor(0, 30, 15); doc.setFontSize(5.5); doc.setFont("helvetica", "bold");
      doc.text(t.recommended, M + 17, y + 9, { align: "center" });
      doc.setTextColor(...ACC); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text(mLabel, M + 34, y + 9);
      doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.text(stratDescLines, M + 34, y + 16);
      y += stratBoxH + 8;

      // 3 scenario cards: Conservative / Balanced / Aggressive
      doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.text(t.yourScenarios, M, y); y += 6;
      const scW = (CW - 8) / 3;
      userScenarios.forEach((s, i) => {
        const cx = M + i * (scW + 4);
        const isRec = s.label === recScenario.label;
        doc.setFillColor(...(isRec ? [0, 22, 13] : SURF));
        doc.roundedRect(cx, y, scW, 34, 2, 2, "F");
        doc.setDrawColor(...(isRec ? ACC : [35, 35, 50]));
        doc.setLineWidth(isRec ? 1.2 : 0.2);
        doc.roundedRect(cx, y, scW, 34, 2, 2, "S");
        const nameY = y + 8;
        doc.setTextColor(...(isRec ? ACC : TEXT)); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text(`${fmtL(s.extra)}/mo`, cx + scW / 2, nameY, { align: "center" });
        doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(s.label, cx + scW / 2, nameY + 6, { align: "center" });
        doc.setTextColor(...(isRec ? ACC : TEXT)); doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text(fmtMonth(s.r.months), cx + scW / 2, nameY + 14, { align: "center" });
        doc.setTextColor(...MUTED); doc.setFontSize(6); doc.setFont("helvetica", "normal");
        doc.text(t.saves(fmtL(s.intSaved)), cx + scW / 2, nameY + 20, { align: "center" });
      });
      y += 42;

      // Divider
      doc.setDrawColor(...SURF); doc.setLineWidth(0.3); doc.line(M, y, W - M, y);
      y += 8;

      // Debt list header
      doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.text(t.yourDebts, M, y); y += 6;

      // Debt rows
      activDebtsP1.forEach((d, i) => {
        if (y > 272) return;
        const bal = parseFloat(d.balance) || 0;
        const rate = parseFloat(d.rate) || 0;
        if (i > 0) { doc.setDrawColor(35, 35, 50); doc.setLineWidth(0.2); doc.line(M, y - 2, W - M, y - 2); }
        doc.setTextColor(...TEXT); doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
        doc.text(pdfText(d.type) || "Debt", M, y + 5);
        doc.setTextColor(...TEXT); doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
        doc.text(fmtL(bal), W - M, y + 5, { align: "right" });
        doc.setTextColor(...RED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(`${rate}% APR`, W - M, y + 11, { align: "right" });
        y += 14;
      });

      pageFooter();

      // ── PAGE 2: ALL 4 METHODS COMPARED ──
      doc.addPage();
      pageHeader(2, t.p2Title);
      y = 36;

      // Page 2 calculations
      const p2Methods = [
        { key: "avalanche", label: t.methodLabels.avalanche, desc: t.methodDescs.avalanche },
        { key: "snowball",  label: t.methodLabels.snowball,  desc: t.methodDescs.snowball  },
        { key: "highbal",   label: t.methodLabels.highbal,   desc: t.methodDescs.highbal   },
        { key: "lowint",    label: t.methodLabels.lowint,    desc: t.methodDescs.lowint    },
      ];
      const p2Results = p2Methods.map(m => ({ ...m, r: results[m.key] }));
      const worstInterest = Math.max(...p2Results.map(m => m.r.totalInterest));
      const bestInterest  = Math.min(...p2Results.map(m => m.r.totalInterest));
      const bestP2 = p2Results.find(m => m.r.totalInterest === bestInterest);
      const worstP2 = p2Results.find(m => m.r.totalInterest === worstInterest);
      const interestSavedVsWorst = worstInterest - results[method].totalInterest;
      const sortedByInterest = [...p2Results].sort((a, b) => a.r.totalInterest - b.r.totalInterest);
      const interestRank = {};
      sortedByInterest.forEach((m, i) => { interestRank[m.key] = i; });
      const barColor = (key) => {
        const rank = interestRank[key];
        if (rank === 0) return [0, 204, 122];
        if (rank === p2Results.length - 1) return [255, 77, 109];
        return [255, 152, 0];
      };

      // Subtitle
      doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.text(t.basedOn(fmtL(totalDebt), fmtL(extraVal)), M, y);
      y += 10;

      // 2x2 card grid
      const cardW = (CW - 8) / 2;
      const cardH = 58;
      p2Results.forEach((m, i) => {
        const col = i % 2, row = Math.floor(i / 2);
        const cx = M + col * (cardW + 8);
        const cy = y + row * (cardH + 6);
        const isBest = m.key === bestP2.key;
        const isSelected = m.key === method;

        // Card background
        doc.setFillColor(...(isBest ? [0, 22, 13] : SURF)); doc.roundedRect(cx, cy, cardW, cardH, 2, 2, "F");
        if (isBest) {
          doc.setDrawColor(0, 204, 122); doc.setLineWidth(1.2); doc.roundedRect(cx, cy, cardW, cardH, 2, 2, "S");
        } else if (isSelected) {
          doc.setDrawColor(...ACC); doc.setLineWidth(0.4); doc.roundedRect(cx, cy, cardW, cardH, 2, 2, "S");
        }

        const nameY = cy + 8;

        // Method name
        doc.setTextColor(isBest ? 0 : TEXT[0], isBest ? 204 : TEXT[1], isBest ? 122 : TEXT[2]);
        doc.setFontSize(10); doc.setFont("helvetica", "bold");
        doc.text(m.label, cx + 4, nameY);

        // Description
        doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(m.desc, cx + 4, nameY + 6);

        // Stat row 1: debt-free in
        doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(t.debtFreeIn, cx + 4, nameY + 15);
        doc.setTextColor(...TEXT); doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
        doc.text(t.fmtDuration(m.r.months), cx + cardW - 4, nameY + 15, { align: "right" });

        // Stat row 2: total interest
        doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(t.totalInterest, cx + 4, nameY + 22);
        doc.setTextColor(...RED); doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
        doc.text(fmtL(m.r.totalInterest), cx + cardW - 4, nameY + 22, { align: "right" });

        // Bar chart
        const barMaxW = cardW - 8;
        const bw = Math.max(3, (m.r.totalInterest / worstInterest) * barMaxW);
        const bc = barColor(m.key);
        doc.setTextColor(...MUTED); doc.setFontSize(6); doc.setFont("helvetica", "normal");
        doc.text(t.interestVsWorst, cx + 4, nameY + 30);
        doc.setFillColor(25, 25, 38); doc.rect(cx + 4, nameY + 32, barMaxW, 3.5, "F");
        doc.setFillColor(...bc); doc.rect(cx + 4, nameY + 32, bw, 3.5, "F");
      });

      y += 2 * (cardH + 6) + 6;

      // Note box
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      const summaryLines = doc.splitTextToSize(t.recommendedSummary(mLabel, fmtL(methodSavingsVsWorst), worstMethodLabel, fmtL(extraVal)), CW - 8);
      const summaryBoxH = Math.max(20, 10 + summaryLines.length * 5);
      doc.setFillColor(0, 35, 20); doc.roundedRect(M, y, CW, summaryBoxH, 2, 2, "F");
      doc.setDrawColor(0, 204, 122); doc.setLineWidth(0.3); doc.roundedRect(M, y, CW, summaryBoxH, 2, 2, "S");
      doc.setTextColor(0, 229, 160); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text(t.summary, M + 4, y + 7);
      doc.setTextColor(160, 200, 175); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
      doc.text(summaryLines, M + 4, y + 14);

      pageFooter();

      // ── PAGE 3: WHICH DEBT TO ATTACK FIRST ──
      doc.addPage();
      pageHeader(3, t.p3Title);
      y = 36;

      // Page 3 calculations
      const p3Sorted = [...debts].filter(d => parseFloat(d.balance) > 0)
        .sort((a, b) => method === "avalanche" ? parseFloat(b.rate) - parseFloat(a.rate)
          : method === "snowball"  ? parseFloat(a.balance) - parseFloat(b.balance)
          : method === "highbal"   ? parseFloat(b.balance) - parseFloat(a.balance)
          : parseFloat(a.rate) - parseFloat(b.rate));

      const p3MethodDesc = t.methodDescsLong[method];
      const p3ConsistencyText = t.consistency[method](fmtL(extraVal));

      // Subtitle
      doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.text(`${mLabel} Method - ${p3MethodDesc}`, M, y);
      y += 10;

      const circleR = 5;
      const cardH3 = 30;

      p3Sorted.forEach((d, i) => {
        if (y > 255) return;
        const bal  = parseFloat(d.balance) || 0;
        const rate = parseFloat(d.rate) || 0;
        const minP = parseFloat(d.minPayment) || 0;
        const isFirst = i === 0;

        // Separator
        if (i > 0) { doc.setDrawColor(35, 35, 50); doc.setLineWidth(0.2); doc.line(M, y, W - M, y); y += 3; }

        // Rank circle
        const cx3 = M + circleR + 1;
        const cy3 = y + cardH3 / 2;
        if (isFirst) {
          doc.setFillColor(0, 204, 122); doc.circle(cx3, cy3, circleR, "F");
          doc.setTextColor(0, 30, 15); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        } else {
          doc.setFillColor(31, 41, 55); doc.circle(cx3, cy3, circleR, "F");
          doc.setTextColor(...MUTED); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        }
        doc.text(`${i + 1}`, cx3, cy3 + 1, { align: "center" });

        // Middle: name + explanation
        const midX = M + circleR * 2 + 6;
        const rightX = W - M;
        const midW = rightX - midX - 38;

        doc.setTextColor(...TEXT); doc.setFontSize(9); doc.setFont("helvetica", "bold");
        doc.text(pdfText(d.type) || "Debt", midX, y + 8);

        let expl1, expl2;
        if (isFirst) {
          const reason = method === "avalanche" ? t.reasonHighestRate(rate) :
            method === "snowball"  ? t.reasonSmallestBal(fmtL(bal)) :
            method === "highbal"   ? t.reasonLargestBal(fmtL(bal)) :
            t.reasonLowestRate(rate);
          expl1 = t.attackFirst(reason, fmtL(extraVal));
          expl2 = t.payMinimumsOthers;
        } else if (i === 1) {
          expl1 = t.attackSecond(pdfText(p3Sorted[0].type) || "Debt #1");
          expl2 = t.rollPaymentHere(fmtL(parseFloat(p3Sorted[0].minPayment) || 0));
        } else {
          expl1 = t.attackN(i + 1, i);
          expl2 = t.rollFreed;
        }

        doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        const expl1Lines = doc.splitTextToSize(expl1, midW);
        doc.text(expl1Lines.slice(0, 1), midX, y + 16);
        const expl2Lines = doc.splitTextToSize(expl2, midW);
        doc.text(expl2Lines.slice(0, 1), midX, y + 22);

        // Right: primary metric + balance/min
        const showRate = method === "avalanche" || method === "snowball";
        const primaryVal = showRate ? `${rate}%` : fmtL(bal);
        const primaryLabel = showRate ? t.apr : t.balance;

        doc.setTextColor(...RED); doc.setFontSize(11); doc.setFont("helvetica", "bold");
        doc.text(primaryVal, rightX, y + 9, { align: "right" });
        doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(primaryLabel, rightX, y + 14, { align: "right" });
        doc.text(showRate ? fmtL(bal) : `${rate}% APR`, rightX, y + 20, { align: "right" });
        doc.text(t.minPerMo(fmtL(minP)), rightX, y + 26, { align: "right" });

        y += cardH3;
      });

      y += 6;

      // Bottom note box (light blue background)
      const noteLines = doc.splitTextToSize(p3ConsistencyText, CW - 10);
      const noteH = 8 + noteLines.length * 4.5;
      doc.setFillColor(10, 20, 45); doc.roundedRect(M, y, CW, noteH, 2, 2, "F");
      doc.setDrawColor(...BLUE); doc.setLineWidth(0.3); doc.roundedRect(M, y, CW, noteH, 2, 2, "S");
      doc.setTextColor(...BLUE); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.text(noteLines, M + 5, y + 7);

      pageFooter();

      // ── PAGE 4: WHAT IF YOU PAY MORE? ──
      doc.addPage();
      pageHeader(4, t.p4Title);
      y = 36;

      // Page 4 calculations — marginal analysis of all 3 user scenarios
      const p4Base = calcPayoff(debts, 0, method);
      const p4Rows = [
        { label: t.minimumsOnlyRow(fmtL(0)), extra: 0, isBase: true, scenarioLabel: "" },
        ...userScenarios.map(s => ({ label: t.scenarioRow(fmtL(s.extra), s.label), extra: s.extra, isBase: false, scenarioLabel: s.label, isBestValue: s.label === recScenario.label })),
      ].map((s, i, arr) => {
        const r = calcPayoff(debts, s.extra, method);
        const intSaved = p4Base.totalInterest - r.totalInterest;
        const moSaved  = p4Base.months - r.months;
        const prevInt  = i === 0 ? p4Base.totalInterest : calcPayoff(debts, arr[i-1].extra, method).totalInterest;
        const marginal = i === 0 ? 0 : prevInt - r.totalInterest;
        return { ...s, r, intSaved, moSaved, marginal };
      });

      // Subtitle
      doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.text(t.marginalAnalysis(mLabel, fmtL(sweetSpotExtra)), M, y);
      y += 10;

      // Table columns — widths sum to CW=178
      const tCols = [
        { x: M,       w: 54, label: t.scenarioCol      },
        { x: M + 54,  w: 24, label: t.debtFreeCol      },
        { x: M + 78,  w: 32, label: t.totalInterestCol },
        { x: M + 110, w: 30, label: t.totalSavedCol    },
        { x: M + 140, w: 38, label: t.marginalGainCol  },
      ];
      const tRowH = 13;

      // Header row
      doc.setFillColor(13, 17, 23); doc.rect(M, y, CW, 8, "F");
      tCols.forEach((c, ci) => {
        doc.setTextColor(...ACC); doc.setFontSize(5.5); doc.setFont("helvetica", "bold");
        const tx = ci === 0 ? c.x + 2 : c.x + c.w - 2;
        doc.text(c.label.toUpperCase(), tx, y + 5.5, { align: ci === 0 ? "left" : "right" });
      });
      y += 8;

      // Data rows
      p4Rows.forEach((row, i) => {
        const isRec = row.isBestValue;
        if (isRec) {
          doc.setFillColor(0, 22, 13); doc.rect(M, y, CW, tRowH, "F");
          doc.setDrawColor(...ACC); doc.setLineWidth(1.2); doc.rect(M, y, CW, tRowH, "S");
        } else {
          doc.setFillColor(i % 2 === 0 ? 16 : 13, i % 2 === 0 ? 16 : 13, i % 2 === 0 ? 22 : 18);
          doc.rect(M, y, CW, tRowH, "F");
        }

        const fw = isRec ? "bold" : "normal";
        const tc = isRec ? ACC : TEXT;

        // Col 0: Scenario label
        doc.setTextColor(...tc); doc.setFontSize(7); doc.setFont("helvetica", fw);
        const clipped = doc.splitTextToSize(row.label, tCols[0].w - 4);
        doc.text(clipped[0], tCols[0].x + 2, y + 8.5);

        const dataY = y + 8.5;

        // Col 1: Debt-Free
        doc.setTextColor(...tc); doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text(t.fmtDuration(row.r.months), tCols[1].x + tCols[1].w - 2, dataY, { align: "right" });

        // Col 2: Total Interest
        doc.setTextColor(...RED); doc.setFontSize(7); doc.setFont("helvetica", fw);
        doc.text(fmtL(row.r.totalInterest), tCols[2].x + tCols[2].w - 2, dataY, { align: "right" });

        // Col 3: Total Saved
        doc.setTextColor(...(row.isBase ? MUTED : ACC)); doc.setFontSize(7); doc.setFont("helvetica", fw);
        doc.text(row.isBase ? "--" : fmtL(row.intSaved), tCols[3].x + tCols[3].w - 2, dataY, { align: "right" });

        // Col 4: Marginal Gain
        doc.setTextColor(...(row.isBase || row.marginal <= 0 ? MUTED : (isRec ? ACC : MUTED)));
        doc.setFontSize(7); doc.setFont("helvetica", fw);
        doc.text(row.isBase || row.marginal <= 0 ? "--" : `+${fmtL(row.marginal)}`, tCols[4].x + tCols[4].w - 2, dataY, { align: "right" });

        y += tRowH;
      });

      y += 10;

      // Insight box
      const recRow = p4Rows.find(r => r.isBestValue);
      const aggRow = p4Rows[p4Rows.length - 1];
      const extraGainAgg = recRow ? aggRow.intSaved - recRow.intSaved : 0;
      const noteL1 = recRow
        ? t.sweetSpotNote(fmtL(sweetSpotExtra))
        : t.payAsMuchNote;
      const noteL2 = recRow && extraGainAgg > 0
        ? t.aggressiveNote(fmtL(extraGainAgg), fmtL(aggRow.extra - recRow.extra))
        : t.bonusNote;
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      const p4NoteL1Lines = doc.splitTextToSize(noteL1, CW - 10);
      const p4NoteL2Lines = doc.splitTextToSize(noteL2, CW - 10);
      const p4NoteLineH = 4.5;
      const p4NoteBoxH = 5 + p4NoteL1Lines.length * p4NoteLineH + 3 + p4NoteL2Lines.length * p4NoteLineH + 4;
      doc.setFillColor(10, 20, 45); doc.roundedRect(M, y, CW, p4NoteBoxH, 2, 2, "F");
      doc.setDrawColor(...BLUE); doc.setLineWidth(0.3); doc.roundedRect(M, y, CW, p4NoteBoxH, 2, 2, "S");
      doc.setTextColor(...BLUE);
      doc.text(p4NoteL1Lines, M + 5, y + 7);
      doc.text(p4NoteL2Lines, M + 5, y + 7 + p4NoteL1Lines.length * p4NoteLineH + 3);

      pageFooter();

      // ── PAGE 5: MONTH-BY-MONTH SCHEDULE ──

      // Build detailed per-debt schedule
      const p5SortedDebts = [...debts].filter(d => parseFloat(d.balance) > 0)
        .sort((a, b) => method === "avalanche" ? parseFloat(b.rate) - parseFloat(a.rate)
          : method === "snowball"  ? parseFloat(a.balance) - parseFloat(b.balance)
          : method === "highbal"   ? parseFloat(b.balance) - parseFloat(a.balance)
          : parseFloat(a.rate) - parseFloat(b.rate));

      let p5Rem = p5SortedDebts.map(d => ({
        name: d.type || "Debt",
        bal:  parseFloat(d.balance) || 0,
        rate: (parseFloat(d.rate) || 0) / 100 / 12,
        minP: parseFloat(d.minPayment) || 0,
      }));

      const p5Entries = [];
      let p5Mo = 0;
      while (p5Rem.length > 0 && p5Mo < 600) {
        p5Mo++;
        const prevBal = p5Rem.reduce((s, d) => s + d.bal, 0);
        let totalInt = 0;
        p5Rem = p5Rem.map(d => { const i = d.bal * d.rate; totalInt += i; return { ...d, bal: d.bal + i }; });
        p5Rem = p5Rem.map(d => ({ ...d, bal: Math.max(0, d.bal - d.minP) }));
        if (p5Rem.length > 0) { const e = Math.min(extraVal, p5Rem[0].bal); p5Rem[0] = { ...p5Rem[0], bal: p5Rem[0].bal - e }; }
        const newBal = p5Rem.reduce((s, d) => s + d.bal, 0);
        const payment = prevBal + totalInt - newBal;
        const focusName = p5Rem[0]?.name || "";
        const paidDebts  = p5Rem.filter(d => d.bal <= 0.01);
        const activeRem  = p5Rem.filter(d => d.bal >  0.01);
        const nextName   = paidDebts.length > 0 ? activeRem[0]?.name || null : null;
        p5Entries.push({ month: p5Mo, focusName, payment, principal: payment - totalInt, interest: totalInt, remaining: newBal, paidOff: paidDebts.length > 0 ? { names: paidDebts.map(d => d.name), nextName } : null });
        if (paidDebts.length > 0) {
          const rolled = paidDebts.reduce((s, d) => s + d.minP, 0);
          p5Rem = activeRem;
          if (p5Rem.length > 0) p5Rem[0] = { ...p5Rem[0], minP: p5Rem[0].minP + rolled };
        } else { p5Rem = activeRem; }
      }

      const getMonthStr = (mn) => { const d = new Date(startDate.getFullYear(), startDate.getMonth() + mn - 1, 1); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
      const debtFreeMonthStr = getMonthStr(p5Mo);
      const totalIntPaid = p5Entries.reduce((s, e) => s + e.interest, 0);
      const numDebts = p5SortedDebts.length;

      // Sub-page header helper (white page, dark header bar)
      const subLabels = ['5a','5b','5c','5d','5e','5f'];
      let p5SubIdx = 0;
      const drawP5Header = () => {
        doc.setFillColor(...BG); doc.rect(0, 0, W, 297, "F");
        doc.setFillColor(...ACC); doc.rect(0, 0, W, 2, "F");
        doc.setFillColor(...SURF); doc.rect(0, 2, W, 20, "F");
        doc.setTextColor(...ACC); doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text("ClearMyDebt", M, 11);
        doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text(`Page ${subLabels[p5SubIdx] || "5"} of ${TOTAL}  |  Generated ${MONTHS[now.getMonth()]} ${now.getFullYear()}  |  ${t.preparedBy}`, W - M, 11, { align: "right" });
        doc.setTextColor(...TEXT); doc.setFontSize(13); doc.setFont("helvetica", "bold");
        doc.text(t.p5Title, M, 28);
      };

      // Table column definitions
      const p5Cols = [
        { x: M,       w: 22, label: t.monthCol,     right: false },
        { x: M + 22,  w: 38, label: t.focusDebt,    right: false },
        { x: M + 60,  w: 28, label: t.paymentCol,   right: true  },
        { x: M + 88,  w: 28, label: t.principalCol, right: true  },
        { x: M + 116, w: 28, label: t.interestCol,  right: true  },
        { x: M + 144, w: 34, label: t.remainingCol, right: true  },
      ];
      const drawP5TableHeader = (yy) => {
        doc.setFillColor(13, 17, 23); doc.rect(M, yy, CW, 8, "F");
        p5Cols.forEach(c => {
          doc.setTextColor(0, 229, 160); doc.setFontSize(6); doc.setFont("helvetica", "bold");
          const tx = c.right ? c.x + c.w - 2 : c.x + 2;
          doc.text(c.label.toUpperCase(), tx, yy + 5.5, { align: c.right ? "right" : "left" });
        });
      };

      doc.addPage();
      drawP5Header();
      y = 36;

      // Subtitle
      doc.setTextColor(107, 107, 138); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.text(t.methodAllDebts(mLabel, p5Mo), M, y);
      y += 8;

      drawP5TableHeader(y); y += 8;

      const p5RowH = 6.5;
      const specialRowH = 8;

      p5Entries.forEach((entry, ei) => {
        // New sub-page if needed
        if (y + p5RowH > 262) {
          pageFooter();
          doc.addPage();
          p5SubIdx++;
          drawP5Header();
          y = 36;
          drawP5TableHeader(y); y += 8;
        }

        // Alternating row bg
        doc.setFillColor(...(ei % 2 === 0 ? SURF : BG));
        doc.rect(M, y, CW, p5RowH, "F");

        // Month
        doc.setTextColor(...TEXT); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
        doc.text(getMonthStr(entry.month), p5Cols[0].x + 2, y + 4.5);
        // Focus Debt
        doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal");
        doc.text(entry.focusName, p5Cols[1].x + 2, y + 4.5);
        // Payment
        doc.setTextColor(...TEXT);
        doc.text(fmtL(entry.payment), p5Cols[2].x + p5Cols[2].w - 2, y + 4.5, { align: "right" });
        // Principal
        doc.setTextColor(...ACC);
        doc.text(fmtL(entry.principal), p5Cols[3].x + p5Cols[3].w - 2, y + 4.5, { align: "right" });
        // Interest
        doc.setTextColor(...RED);
        doc.text(fmtL(entry.interest), p5Cols[4].x + p5Cols[4].w - 2, y + 4.5, { align: "right" });
        // Remaining
        doc.setTextColor(...TEXT); doc.setFont("helvetica", "bold");
        doc.text(fmtL(entry.remaining), p5Cols[5].x + p5Cols[5].w - 2, y + 4.5, { align: "right" });

        y += p5RowH;

        // PAID OFF row
        if (entry.paidOff) {
          if (y + specialRowH > 262) { pageFooter(); doc.addPage(); p5SubIdx++; drawP5Header(); y = 36; drawP5TableHeader(y); y += 8; }
          doc.setFillColor(0, 40, 25); doc.rect(M, y, CW, specialRowH, "F");
          doc.setDrawColor(...ACC); doc.setLineWidth(0.2); doc.rect(M, y, CW, specialRowH, "S");
          doc.setTextColor(...ACC); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
          const paidMsg = entry.paidOff.nextName
            ? t.paidOffRolling(entry.paidOff.names.join(", "), entry.paidOff.nextName)
            : t.paidOff(entry.paidOff.names.join(", "));
          doc.text(paidMsg, M + 4, y + 5.5);
          y += specialRowH;
        }
      });

      // DEBT FREE final row
      if (y + specialRowH > 262) { pageFooter(); doc.addPage(); p5SubIdx++; drawP5Header(); y = 36; drawP5TableHeader(y); y += 8; }
      doc.setFillColor(0, 204, 122); doc.rect(M, y, CW, specialRowH, "F");
      doc.setTextColor(0, 30, 15); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text(t.debtFreeRow(debtFreeMonthStr), M + 4, y + 5.5);
      y += specialRowH + 8;

      // Footer note
      if (y + 10 < 278) {
        doc.setTextColor(107, 107, 138); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
        doc.text(t.allDebtsNote(numDebts, debtFreeMonthStr, fmtL(totalIntPaid)), M, y);
      }

      pageFooter();

      // ── PAGE 6: DEBT CONSOLIDATION ANALYSIS ──
      doc.addPage();
      pageHeader(6, t.p6Title);
      y = 36;

      // Calculations — use the same extraVal + method as the rest of the report
      const p6CurrentInterest = act.totalInterest;
      const p6TotalDebt = debts.reduce((s, d) => s + (parseFloat(d.balance) || 0), 0);
      const p6TotalMonths = Math.max(act.months, 1);
      const p6Rates = [0.10, 0.12, 0.15];
      const p6OriginFee = 500;
      const p6Scenarios = p6Rates.map(rate => {
        const mr = rate / 12;
        const monthlyPayment = p6TotalDebt * mr / (1 - Math.pow(1 + mr, -p6TotalMonths));
        const totalPaid = monthlyPayment * p6TotalMonths;
        const totalInterest = totalPaid - p6TotalDebt;
        const interestDiff = p6CurrentInterest - totalInterest;
        const isBetter = interestDiff > 0;
        const currentMonthly = minTot + extraVal;
        const monthlySaving = currentMonthly - monthlyPayment;
        const breakEvenMonths = (isBetter && monthlySaving > 0) ? Math.ceil(p6OriginFee / monthlySaving) : null;
        return { rate, monthlyPayment, totalInterest, interestDiff, isBetter, breakEvenMonths, monthlySaving };
      });
      const p6BetterScenarios = p6Scenarios.filter(s => s.isBetter);
      const p6BreakEvenRate = p6BetterScenarios.length > 0
        ? Math.round(p6BetterScenarios[p6BetterScenarios.length - 1].rate * 100)
        : null;

      // Subtitle
      doc.setTextColor(...MUTED); doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
      doc.text(t.isItWorthIt, M, y);
      y += 12;

      // 3-column cards
      const p6CardW = (CW - 8) / 3;
      const p6CardH = 48;
      p6Scenarios.forEach((s, i) => {
        const cx = M + i * (p6CardW + 4);
        const cy = y;
        const borderCol = s.isBetter ? [0, 204, 122] : [252, 100, 100];
        const fillCol = s.isBetter ? [0, 25, 15] : [35, 8, 8];
        doc.setFillColor(...fillCol); doc.roundedRect(cx, cy, p6CardW, p6CardH, 2, 2, "F");
        doc.setDrawColor(...borderCol); doc.setLineWidth(0.5);
        doc.roundedRect(cx, cy, p6CardW, p6CardH, 2, 2, "S");
        // Rate
        doc.setTextColor(...borderCol); doc.setFontSize(20); doc.setFont("helvetica", "bold");
        doc.text(`${Math.round(s.rate * 100)}%`, cx + p6CardW / 2, cy + 15, { align: "center" });
        // APR label
        doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        doc.text(t.aprConsolidationLoan, cx + p6CardW / 2, cy + 21, { align: "center" });
        // Total interest
        const intCol = s.isBetter ? [0, 204, 122] : [255, 77, 109];
        doc.setTextColor(...intCol); doc.setFontSize(8); doc.setFont("helvetica", "bold");
        doc.text(fmtL(s.totalInterest), cx + p6CardW / 2, cy + 30, { align: "center" });
        doc.setTextColor(...MUTED); doc.setFontSize(6); doc.setFont("helvetica", "normal");
        doc.text(t.totalInterestLabel, cx + p6CardW / 2, cy + 35, { align: "center" });
        // Verdict pill
        const verdictText = s.isBetter
          ? t.savesAmount(fmtL(s.interestDiff))
          : t.costsMore(fmtL(Math.abs(s.interestDiff)));
        doc.setFillColor(...(s.isBetter ? [0, 204, 122] : [255, 77, 109]));
        doc.roundedRect(cx + 4, cy + 38, p6CardW - 8, 8, 1.5, 1.5, "F");
        doc.setTextColor(s.isBetter ? 0 : 255, s.isBetter ? 0 : 255, s.isBetter ? 0 : 255);
        doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text(verdictText, cx + p6CardW / 2, cy + 43, { align: "center" });
      });
      y += p6CardH + 12;

      // Break-even table
      const p6TableH = 12 + p6Scenarios.length * 12 + 4;
      doc.setFillColor(20, 20, 28); doc.roundedRect(M, y, CW, p6TableH, 2, 2, "F");
      doc.setTextColor(...TEXT); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text(t.breakEvenPoint(fmtL(p6OriginFee)), M + 4, y + 9);
      const p6TableBodyY = y + 14;
      p6Scenarios.forEach((s, i) => {
        const rowY = p6TableBodyY + i * 12;
        let rowText, rowCol;
        if (!s.isBetter) {
          rowText = t.neverBreaksEven;
          rowCol = RED;
        } else if (s.breakEvenMonths === null || s.breakEvenMonths > p6TotalMonths) {
          rowText = t.breakEvenAfterPayoff;
          rowCol = YEL;
        } else if (s.breakEvenMonths <= Math.round(p6TotalMonths / 3)) {
          rowText = t.breakEvenSaves(s.breakEvenMonths, fmtL(Math.abs(s.monthlySaving)));
          rowCol = [0, 204, 122];
        } else {
          rowText = t.breakEvenBarely(s.breakEvenMonths);
          rowCol = YEL;
        }
        doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
        doc.text(t.atApr(Math.round(s.rate * 100)), M + 4, rowY + 7);
        doc.setTextColor(...rowCol); doc.setFont("helvetica", "normal");
        doc.text(rowText, M + 38, rowY + 7);
      });
      y += p6TableH + 10;

      // Recommendation box
      const p6HighCard = debts.find(d => parseFloat(d.rate) > 15);
      const p6RecLines = [];
      p6RecLines.push(p6BreakEvenRate !== null
        ? t.consolidationBelowApr(p6BreakEvenRate)
        : t.noneConsolidationRates);
      if (p6HighCard) {
        p6RecLines.push(t.debtExpensive(p6HighCard.type || "debt", p6HighCard.rate));
        p6RecLines.push(t.qualifyConsolidation);
        p6RecLines.push(t.stopUsingCard);
      }
      const p6RecH = 14 + p6RecLines.length * 6 + 4;
      doc.setFillColor(13, 17, 23); doc.roundedRect(M, y, CW, p6RecH, 2, 2, "F");
      doc.setDrawColor(0, 204, 122); doc.setLineWidth(0.4);
      doc.roundedRect(M, y, CW, p6RecH, 2, 2, "S");
      doc.setTextColor(0, 204, 122); doc.setFontSize(8.5); doc.setFont("helvetica", "bold");
      doc.text(t.ourRecommendation, M + 4, y + 9);
      doc.setTextColor(...TEXT); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      p6RecLines.forEach((line, i) => { doc.text(line, M + 4, y + 16 + i * 6); });
      y += p6RecH + 4;

      pageFooter();

      // ── PAGE 7: ACTION PLAN + FAQ ──
      doc.addPage();
      pageHeader(7, t.p7Title);
      y = 36;

      // Calculations
      const p7Sorted = [...debts].filter(d => parseFloat(d.balance) > 0)
        .sort((a, b) => method === "avalanche" ? parseFloat(b.rate) - parseFloat(a.rate)
          : method === "snowball"  ? parseFloat(a.balance) - parseFloat(b.balance)
          : method === "highbal"   ? parseFloat(b.balance) - parseFloat(a.balance)
          : parseFloat(a.rate) - parseFloat(b.rate));
      let p7Rem = p7Sorted.map(d => ({
        name: pdfText(d.type) || "Debt",
        bal: parseFloat(d.balance) || 0,
        rate: (parseFloat(d.rate) || 0) / 100 / 12,
        minP: parseFloat(d.minPayment) || 0,
      }));
      const p7PayoffMonths = {};
      let p7Mo = 0;
      while (p7Rem.length > 0 && p7Mo < 600) {
        p7Mo++;
        p7Rem = p7Rem.map(d => ({ ...d, bal: d.bal + d.bal * d.rate }));
        p7Rem = p7Rem.map(d => ({ ...d, bal: Math.max(0, d.bal - d.minP) }));
        if (p7Rem.length > 0) p7Rem[0] = { ...p7Rem[0], bal: Math.max(0, p7Rem[0].bal - extraVal) };
        const p7Paid = p7Rem.filter(d => d.bal <= 0.01);
        p7Paid.forEach(d => { if (!p7PayoffMonths[d.name]) p7PayoffMonths[d.name] = p7Mo; });
        if (p7Paid.length) {
          const rolled = p7Paid.reduce((s, d) => s + d.minP, 0);
          p7Rem = p7Rem.filter(d => d.bal > 0.01);
          if (p7Rem.length) p7Rem[0] = { ...p7Rem[0], minP: p7Rem[0].minP + rolled };
        }
      }
      const p7MoToDate = (m) => {
        const d = new Date(startDate.getFullYear(), startDate.getMonth() + (m - 1), 1);
        return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      };
      const p7TotalInterestSaved = Math.max(0, zeroExtraResult.totalInterest - act.totalInterest);
      const p7DebtFreeDate = p7MoToDate(Math.max(act.months, 1));
      const p7D1 = p7Sorted[0];
      const p7D2 = p7Sorted[1];
      const p7D3 = p7Sorted[2];
      const p7D1Name = pdfText(p7D1 ? (p7D1.type || "Debt 1") : "");
      const p7D2Name = pdfText(p7D2 ? (p7D2.type || "Debt 2") : "");
      const p7D3Name = pdfText(p7D3 ? (p7D3.type || "Debt 3") : "");
      const p7D1Min = parseFloat(p7D1?.minPayment) || 0;
      const p7D2Min = parseFloat(p7D2?.minPayment) || 0;
      const p7D1Combined = p7D1Min + extraVal;
      const p7D2Combined = p7D1Combined + p7D2Min;
      const p7TotalFreed = debts.reduce((s, d) => s + (parseFloat(d.minPayment) || 0), 0) + extraVal;
      const p7D1PayoffDate = p7D1 && p7PayoffMonths[p7D1Name] ? p7MoToDate(p7PayoffMonths[p7D1Name]) : "soon";
      const p7D2PayoffDate = p7D2 && p7PayoffMonths[p7D2Name] ? p7MoToDate(p7PayoffMonths[p7D2Name]) : "later";
      const nowMonthYear = `${MONTHS[now.getMonth()]} ${now.getFullYear()}`;
      const p7Steps = [];
      if (p7D1) p7Steps.push(t.step1(p7D1Name, fmtL(extraVal), fmtL(p7D1Combined)));
      if (p7D2) p7Steps.push(t.step2(p7D1PayoffDate, p7D1Name, fmtL(p7D1Combined), p7D2Name, fmtL(p7D2Combined)));
      if (p7D3) p7Steps.push(t.step3withD3(p7D2PayoffDate, p7D2Name, fmtL(p7TotalFreed), p7D3Name));
      else if (p7D2) p7Steps.push(t.step3noD3(p7D2PayoffDate, p7D2Name));

      // Subtitle
      doc.setTextColor(...MUTED); doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.text(t.stepsToFreedom, M, y); y += 10;

      // Pre-calculate step lines for box height
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      const p7StepLines = p7Steps.map(s => doc.splitTextToSize(s, CW - 18));
      const p7BoxBodyH = p7StepLines.reduce((s, l) => s + l.length * 4 + 6, 0);
      const p7BoxH = 10 + p7BoxBodyH;

      // Action plan box
      doc.setFillColor(13, 17, 23); doc.roundedRect(M, y, CW, p7BoxH, 2, 2, "F");
      doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
      doc.text(t.startingDate(nowMonthYear.toUpperCase()), M + 4, y + 7);
      let sy = y + 12;
      p7StepLines.forEach((lines, i) => {
        doc.setFillColor(0, 204, 122); doc.circle(M + 5, sy + 2.5, 2.5, "F");
        doc.setTextColor(0, 0, 0); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
        doc.text(`${i + 1}`, M + 5, sy + 3.5, { align: "center" });
        doc.setTextColor(...TEXT); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
        doc.text(lines, M + 11, sy + 4);
        sy += lines.length * 4 + 6;
      });
      y += p7BoxH + 5;

      // Debt-free date box
      doc.setFillColor(0, 25, 15); doc.roundedRect(M, y, CW, 16, 2, 2, "F");
      doc.setDrawColor(0, 204, 122); doc.setLineWidth(0.4);
      doc.roundedRect(M, y, CW, 16, 2, 2, "S");
      doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
      doc.text(t.debtFreeDateLabel, M + 4, y + 5);
      doc.setTextColor(0, 229, 160); doc.setFontSize(10); doc.setFont("helvetica", "bold");
      doc.text(p7DebtFreeDate, M + 4, y + 12);
      doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.text(t.youWillSave(fmtL(p7TotalInterestSaved)), W - M - 4, y + 12, { align: "right" });
      y += 22;

      // Divider
      doc.setDrawColor(...SURF); doc.setLineWidth(0.3); doc.line(M, y, W - M, y);
      y += 7;

      // FAQ section
      doc.setTextColor(...TEXT); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text(t.glossaryFaq, M, y);
      doc.setDrawColor(...SURF); doc.setLineWidth(0.3); doc.line(M, y + 2, W - M, y + 2);
      y += 9;

      const p7Faqs = t.faqs;

      p7Faqs.forEach((f) => {
        if (y > 272) return;
        doc.setFontSize(7); doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(f.a, CW - 7);
        const faqH = 5 + Math.min(lines.length, 2) * 4 + 3;
        doc.setFillColor(0, 204, 122); doc.rect(M, y, 1.5, faqH, "F");
        doc.setTextColor(...TEXT); doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
        doc.text(f.q, M + 4, y + 5);
        doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
        doc.text(lines.slice(0, 2), M + 4, y + 10);
        y += faqH + 2;
      });

      pageFooter();

      doc.save("ClearMyDebt-Plan.pdf");
      setDownloaded(true);
    } catch (e) {
      console.error(e);
      alert("Error generating PDF. Please try again.");
    }
    setGenerating(false);
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
    * { box-sizing: border-box; }
    body { margin: 0; background: #0a0a0f; }
    input:focus, select:focus { border-color: #00e5a0 !important; outline: none; }
    input::placeholder { color: #6b6b8a; }
    input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
    button { font-family: 'DM Sans', sans-serif; }
    .btn-h:hover { opacity: 0.88; transform: translateY(-1px); }
    select option { background: #13131a; color: #e8e8f0; }
  `;

  const C = COLORS;
  const S = {
    app: { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'DM Sans', sans-serif" },
    hdr: { padding: "32px 24px 16px", borderBottom: `1px solid ${C.border}`, textAlign: "center", background: "linear-gradient(180deg,#0d0d16,#0a0a0f)" },
    logo: { fontSize: "11px", letterSpacing: "4px", color: C.accent, fontWeight: 700, marginBottom: "4px" },
    title: { fontSize: "clamp(24px,5vw,36px)", fontWeight: 900, letterSpacing: "-1px", background: `linear-gradient(135deg,#fff,${C.accent})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
    wrap: { maxWidth: "680px", margin: "0 auto", padding: "28px 16px 40px" },
    card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "20px", marginBottom: "14px" },
    sT: { fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: C.textMuted, marginBottom: "14px", fontWeight: 600 },
    inp: (err) => ({ background: C.bg, border: `1px solid ${err ? C.red : C.border}`, borderRadius: "8px", padding: "8px 10px", color: C.text, fontSize: "14px", width: "100%", transition: "border-color 0.2s" }),
    sel: (err) => ({ background: C.bg, border: `1px solid ${err ? C.red : C.border}`, borderRadius: "8px", padding: "8px 10px", color: C.text, fontSize: "14px", width: "100%", transition: "border-color 0.2s", cursor: "pointer" }),
    err: { fontSize: "11px", color: C.red, marginTop: "3px" },
    btnP: { background: C.accent, color: C.bg, border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 800, cursor: "pointer", width: "100%", transition: "all 0.2s" },
    btnG: { background: "transparent", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "9px 16px", fontSize: "13px", cursor: "pointer", transition: "all 0.2s" },
    mBtn: (a, c) => ({ padding: "10px 8px", borderRadius: "10px", border: `1px solid ${a ? c : C.border}`, background: a ? `${c}15` : "transparent", color: a ? c : C.textMuted, cursor: "pointer", textAlign: "center", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" }),
    hero: { background: `linear-gradient(135deg,${C.surface},#0d1420)`, border: `1px solid ${C.border}`, borderRadius: "18px", padding: "24px 20px 16px", marginBottom: "14px", textAlign: "center", position: "relative", overflow: "hidden" },
    sg: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "14px" },
    sc: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "14px", textAlign: "center" },
    cta: { background: "linear-gradient(135deg,#0d1a13,#0a0f0d)", border: `1px solid ${C.accentMid}`, borderRadius: "18px", padding: "24px 20px", textAlign: "center", position: "relative", overflow: "hidden" },
  };

  // ── RESULT PAGE ──
  if (step === "result" && active && results) {
    const bestByInt = ALL_METHODS.filter(m => results[m.key]).slice().sort((a, b) => results[a.key].totalInterest - results[b.key].totalInterest)[0];
    // Use pre-computed recommendation (same logic as PDF: best method + sweet spot)
    const sweetSpotExtra = recommendation?.sweetSpotExtra ?? parseFloat(extra) ?? 0;
    const sweetResult = calcPayoff(debts, sweetSpotExtra, method);
    const interestSaved = recommendation?.interestSaved ?? Math.max(0, calcPayoff(debts, 0, method).totalInterest - sweetResult.totalInterest);

    return (
      <div style={S.app}>
        {showHelmet && <Helmet>
          <html lang={localeCfg.lang} />
          <title>{localeCfg.metaTitle}</title>
          <meta name="description" content={localeCfg.metaDesc} />
          <link rel="canonical" href={`https://clearmydebt.app/${locale === 'en-us' ? '' : locale + '/'}`} />
          {Object.keys(LOCALE_CONFIG).map(loc => (
            <link key={loc} rel="alternate" hreflang={loc} href={`https://clearmydebt.app/${loc === 'en-us' ? '' : loc + '/'}`} />
          ))}
          <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/" />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ClearMyDebt",
            "url": "https://clearmydebt.app",
            "description": localeCfg.metaDesc,
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": localeCfg.currency },
            "author": { "@type": "Organization", "name": "Weelco Inc." },
            "inLanguage": localeCfg.lang,
          })}</script>
        </Helmet>}
        <style>{css}</style>
        {showHeader && <div style={S.hdr}>
          <div style={S.logo}>ClearMyDebt</div>
          <div style={S.title}>{u.resultHeaderTitle}</div>
        </div>}
        <div style={S.wrap}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
            {METHODS.map(m => (
              <button key={m.key} className="btn-h" style={S.mBtn(method === m.key, m.color)} onClick={() => setMethod(m.key)}>
                <div style={{ fontWeight: 700, fontSize: "13px" }}>{m.label}</div>
                <div style={{ fontSize: "10px", opacity: 0.7, marginTop: "2px" }}>{m.desc}</div>
              </button>
            ))}
          </div>

          <div style={S.hero}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "80px", background: `radial-gradient(ellipse at 50% -20%,${COLORS[METHODS.find(m=>m.key===method)?.color]||C.accent}18,transparent 70%)` }} />
            <div style={{ fontSize: "11px", letterSpacing: "2px", color: C.textMuted, textTransform: "uppercase", marginBottom: "6px" }}>{u.debtFreeIn}</div>
            <div style={{ fontSize: "clamp(36px,8vw,54px)", fontWeight: 900, color: METHODS.find(m=>m.key===method)?.color || C.accent, letterSpacing: "-2px", lineHeight: 1 }}>{fmtMonth(sweetResult.months)}</div>
            <div style={{ color: C.textMuted, fontSize: "13px", marginTop: "5px" }}>{u.withExtraMethod(fmtL(sweetSpotExtra), METHODS.find(m=>m.key===method)?.label)}</div>
            <div style={{ height: "80px", marginTop: "16px" }}><Chart schedule={sweetResult.schedule} color={METHODS.find(m=>m.key===method)?.color || C.accent} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: C.textMuted, marginTop: "4px" }}>
              <span>{u.chartToday} · {fmtL(totalDebt)}</span><span>{u.chartDebtFree}</span>
            </div>
          </div>

          <div style={S.sg}>
            {[
              { v: fmtL(sweetResult.totalInterest), l: u.totalInterest, c: C.red },
              { v: fmtL(totalDebt), l: u.totalDebtLabel, c: C.yellow },
              { v: fmtL(totalDebt + sweetResult.totalInterest), l: u.totalPaid, c: C.accent },
            ].map((st, i) => (
              <div key={i} style={S.sc}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: st.c, marginBottom: "3px" }}>{st.v}</div>
                <div style={{ fontSize: "10px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "1px" }}>{st.l}</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={S.sT}>{u.methodComparison}</div>
            {ALL_METHODS.filter(m => m.key === "avalanche" || m.key === "snowball").map((m, i, arr) => {
              const mr = calcPayoff(debts, sweetSpotExtra, m.key);
              return (
                <div key={m.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <div>
                    <span style={{ color: m.color, fontWeight: 600, fontSize: "14px" }}>{m.label}</span>
                    <span style={{ color: C.textMuted, fontSize: "12px", marginLeft: "8px" }}>{m.desc}</span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: C.text, fontWeight: 600 }}>{fmtMonth(mr.months)}</div>
                    <div style={{ color: C.textMuted, fontSize: "12px" }}>{fmtL(mr.totalInterest)} {u.interestLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={S.cta}>
            {isPaid ? (
              <>
                <div style={{ fontSize: "18px", fontWeight: 800, color: C.accent, marginBottom: "8px" }}>{u.paymentConfirmed}</div>
                <div style={{ color: C.textMuted, fontSize: "13px", marginBottom: "16px" }}>
                  {downloaded ? u.checkDownloads : u.readyToDownload}
                </div>
                <button className="btn-h" onClick={generatePDF} disabled={generating}
                  style={{ background: C.accent, color: C.bg, border: "none", borderRadius: "12px", padding: "14px 32px", fontSize: "1rem", fontWeight: 700, cursor: "pointer", marginTop: "16px", width: "100%", opacity: generating ? 0.7 : 1, transition: "all 0.2s" }}>
                  {generating ? u.generatingPdf : downloaded ? u.downloadAgain : u.downloadYourPdf}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: "18px", fontWeight: 800, color: C.white, marginBottom: "8px" }}>{u.getFullPlan}</div>
                <div style={{ textAlign: "left", background: "#ffffff08", borderRadius: "10px", padding: "12px", marginBottom: "16px" }}>
                  {[u.pdfItem1, u.pdfItem2, u.pdfItem3, u.pdfItem4(fmtL(extraScenarios[0]), fmtL(extraScenarios[1]), fmtL(extraScenarios[2])), u.pdfItem5, u.pdfItem6, u.pdfItem7].map((item, i) => (
                    <div key={i} style={{ fontSize: "13px", color: C.text, padding: "4px 0", display: "flex", gap: "8px" }}>
                      <span style={{ color: C.accent, fontWeight: 700 }}>+</span>{item}
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: "12px", marginTop: "16px" }}>
                  <span style={{ fontSize: "15px", color: C.textMuted, textDecoration: "line-through", marginRight: "8px" }}>{regionalPrice.original}</span>
                  <span style={{ fontSize: "28px", fontWeight: 700, color: C.text }}>{regionalPrice.display}</span>
                  <span style={{ color: C.textMuted, fontSize: "14px", marginLeft: "6px" }}>{u.priceBadge}</span>
                </div>
                <div style={{ position: "relative" }}>
                  {interestSaved > 50 && (
                    <div style={{ textAlign: "right", marginBottom: "4px" }}>
                      <span style={{
                        display: "inline-block",
                        background: "#ffd166", color: "#000",
                        fontSize: "11px", fontWeight: 700,
                        padding: "2px 10px", borderRadius: "20px",
                        letterSpacing: "0.5px",
                      }}>
                        {u.saves(fmtL(interestSaved))}
                      </span>
                    </div>
                  )}
                  <button className="btn-h" onClick={handleBuyPDF}
                    style={{ background: C.accent, color: C.bg, border: "none", borderRadius: "12px", padding: "14px 32px", fontSize: "1rem", fontWeight: 700, cursor: "pointer", width: "100%", boxShadow: `0 6px 24px ${C.accent}33`, transition: "all 0.2s" }}>
                    {u.downloadPdfBtn}
                  </button>
                </div>
                <div style={{ textAlign: "center", marginTop: "10px", fontSize: "12px", color: C.textMuted }}>
                  {u.notSureYet}{" "}
                  <span
                    onClick={() => setShowSample(true)}
                    style={{ color: C.textMuted, textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}
                    onMouseOver={e => e.target.style.color = C.text}
                    onMouseOut={e => e.target.style.color = C.textMuted}
                  >
                    {u.seeWhatsInside}
                  </span>
                </div>
              </>
            )}
          </div>

          <button className="btn-h" style={{ ...S.btnG, width: "100%", marginTop: "14px", padding: "12px" }} onClick={() => { window.history.pushState({}, "", window.location.pathname); setStep("input"); }}>
            {u.editDebts}
          </button>
        </div>

      {beforeFooter}
      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: "40px 24px 32px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ fontSize: "11px", letterSpacing: "3px", color: C.accent, fontWeight: 700, marginBottom: "28px", textAlign: "center" }}>ClearMyDebt</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: "28px 20px", marginBottom: "32px" }}>

            {/* Calculators column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.calculators}</div>
              {[
                { l: u.nav.debtCalculator, h: localePath(locale, '/') },
                { l: u.nav.creditCard,     h: localePath(locale, '/credit-card-payoff-calculator/') },
                { l: u.nav.snowball,       h: localePath(locale, '/debt-snowball-calculator/') },
                { l: u.nav.avalanche,      h: localePath(locale, '/debt-avalanche-calculator/') },
                { l: u.nav.repayment,      h: localePath(locale, '/debt-repayment-calculator/') },
                { l: u.nav.studentLoan,    h: localePath(locale, '/student-loan-payoff-calculator/') },
                { l: u.nav.personalLoan,   h: localePath(locale, '/personal-loan-payoff-calculator/') },
                { l: u.nav.carLoan,        h: localePath(locale, '/car-loan-payoff-calculator/') },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px", lineHeight: 1.4 }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

            {/* Resources column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.resources}</div>
              {[
                { l: u.nav.about,    h: localePath(locale, '/about') },
                { l: u.nav.faq,      h: localePath(locale, '/faq') },
                { l: u.nav.contact,  h: localePath(locale, '/contact') },
                { l: u.nav.blog,     h: '/blog/' },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px" }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

            {/* Legal column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.legal}</div>
              {[
                { l: u.nav.tos,     h: localePath(locale, '/tos') },
                { l: u.nav.privacy, h: localePath(locale, '/privacy') },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px" }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

          </div>
          <div style={{ fontSize: "12px", color: C.textMuted, textAlign: "center", paddingTop: "20px", borderTop: `1px solid ${C.border}` }}>{u.copyright}</div>
        </div>
      </div>

      {showSample && (
        <div
          onClick={() => setShowSample(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: "16px",
              maxWidth: "560px",
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
            }}
          >
            {/* Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div>
                <div style={{ color: C.text, fontWeight: 700, fontSize: "16px" }}>
                  {u.sampleTitle}
                </div>
                <div style={{ color: C.textMuted, fontSize: "12px", marginTop: "2px" }}>
                  {u.sampleSubtitle}
                </div>
              </div>
              <button
                onClick={() => setShowSample(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: C.textMuted,
                  fontSize: "22px",
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >×</button>
            </div>

            {/* Sample table */}
            <div style={{ padding: "20px 24px" }}>
              <div style={{
                background: C.bg,
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${C.border}`,
                fontSize: "12px",
                fontFamily: "monospace",
              }}>
                {/* Table header */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                  padding: "10px 12px",
                  background: "#13231a",
                  color: C.textMuted,
                  letterSpacing: "0.5px",
                }}>
                  <span>{u.sampleMonth}</span>
                  <span>{u.samplePayment}</span>
                  <span>{u.samplePrincipal}</span>
                  <span>{u.sampleInterest}</span>
                  <span style={{ textAlign: "right" }}>{u.sampleBalance}</span>
                </div>
                {[
                  ["Jan 2025", "$892", "$712", "$180", "$24,788"],
                  ["Feb 2025", "$892", "$715", "$177", "$24,073"],
                  ["Mar 2025", "$892", "$718", "$174", "$23,355"],
                  ["Apr 2025", "$892", "$720", "$172", "$22,635"],
                  ["May 2025", "$892", "$723", "$169", "$21,912"],
                  ["Jun 2025", "$892", "$726", "$166", "$21,186"],
                  ["...", "...", "...", "...", "..."],
                ].map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                      padding: "9px 12px",
                      background: i % 2 === 0 ? "transparent" : "#0d1a11",
                      color: i === 6 ? C.textMuted : C.text,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <span>{row[0]}</span>
                    <span>{row[1]}</span>
                    <span style={{ color: C.accent }}>{row[2]}</span>
                    <span style={{ color: C.red }}>{row[3]}</span>
                    <span style={{ textAlign: "right" }}>{row[4]}</span>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: "12px",
                padding: "10px 14px",
                background: "#0d1a11",
                borderRadius: "8px",
                border: `1px solid ${C.border}`,
                color: C.textMuted,
                fontSize: "11px",
                textAlign: "center",
              }}>
                {u.sampleFullPlan(debts.filter(d => parseFloat(d.balance) > 0).length)}
              </div>
            </div>

            {/* CTA */}
            <div style={{ padding: "0 24px 24px" }}>
              <div style={{ position: "relative" }}>
                {interestSaved > 50 && (
                  <div style={{
                    position: "absolute", top: "-10px", right: "12px",
                    background: "#ffd166", color: "#000",
                    fontSize: "11px", fontWeight: 700,
                    padding: "2px 10px", borderRadius: "20px",
                    letterSpacing: "0.5px", zIndex: 1,
                  }}>
                    {u.saves(fmtL(interestSaved))}
                  </div>
                )}
                <button
                  onClick={() => { setShowSample(false); isPaid ? generatePDF() : handleBuyPDF(); }}
                  style={{
                    width: "100%",
                    padding: "15px",
                    background: C.accent,
                    border: "none",
                    borderRadius: "8px",
                    color: "#000",
                    fontWeight: 700,
                    fontSize: "15px",
                    cursor: "pointer",
                    letterSpacing: "0.3px",
                  }}
                >
                  {isPaid ? u.downloadYourPdf : u.getFullPlanBtn("$7.00")}
                </button>
              </div>
              <div style={{
                textAlign: "center",
                color: C.textMuted,
                fontSize: "11px",
                marginTop: "10px",
              }}>
                {u.secureInstant}
              </div>
            </div>
          </div>
        </div>
      )}
      <CookieBanner locale={locale} />
    </div>
    );
  }

  // ── INPUT PAGE ──
  let firstErrorSet = false;
  return (
    <div style={S.app}>
      {showHelmet && <Helmet>
        <html lang={localeCfg.lang} />
        <title>{localeCfg.metaTitle}</title>
        <meta name="description" content={localeCfg.metaDesc} />
        <link rel="canonical" href={`https://clearmydebt.app/${locale === 'en-us' ? '' : locale + '/'}`} />
        {Object.keys(LOCALE_CONFIG).map(loc => (
          <link key={loc} rel="alternate" hreflang={loc} href={`https://clearmydebt.app/${loc === 'en-us' ? '' : loc + '/'}`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "ClearMyDebt",
          "url": "https://clearmydebt.app",
          "description": localeCfg.metaDesc,
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": localeCfg.currency },
          "author": { "@type": "Organization", "name": "Weelco Inc." },
          "inLanguage": localeCfg.lang,
        })}</script>
      </Helmet>}
      <style>{css}</style>
      <CookieBanner locale={locale} />
      {showHeader && <div style={S.hdr}>
        <div style={S.logo}>ClearMyDebt</div>
        <div style={S.title}>{u.headerTitle}</div>
        <div style={{ color: C.textMuted, fontSize: "14px", marginTop: "8px" }}>{u.headerSubtitle}</div>
      </div>}
      <div style={S.wrap}>
        <div style={S.card}>
          <div style={S.sT}>{u.yourDebts}</div>
          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 90px 28px", gap: "6px", marginBottom: "6px" }}>
            {[u.colType, u.colBalance, u.colRate, u.colMinPay, ""].map((h, i) => (
              <div key={i} style={{ fontSize: "9px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: 600 }}>{h}</div>
            ))}
          </div>
          {debts.map((d) => {
            const isFirst = !firstErrorSet && Object.keys(d.errors).length > 0;
            if (isFirst) firstErrorSet = true;
            const hasErr = Object.keys(d.errors).length > 0;
            return (
              <div key={d.id} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: "10px", marginBottom: "10px" }}>
                {/* Single compact row: Type | Balance | Rate | MinPay | X */}
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 90px 28px", gap: "6px", alignItems: "center" }}>
                  <select
                    style={{ ...S.sel(d.errors.type), padding: "7px 10px" }}
                    value={d.type}
                    disabled={lockedDebtType}
                    ref={isFirst && d.errors.type ? el => { if (el) el.focus(); } : null}
                    onChange={e => updateDebt(d.id, "type", e.target.value)}
                  >
                    {localeDebtTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input
                    style={{ ...S.inp(d.errors.balance), padding: "7px 10px" }}
                    placeholder={u.placeholderBalance}
                    type="number"
                    value={d.balance}
                    ref={isFirst && d.errors.balance && !d.errors.type ? el => { if (el) el.focus(); } : null}
                    onChange={e => updateDebt(d.id, "balance", e.target.value)}
                  />
                  <input
                    style={{ ...S.inp(d.errors.rate), padding: "7px 10px" }}
                    placeholder={u.placeholderRate}
                    type="number"
                    value={d.rate}
                    onChange={e => updateDebt(d.id, "rate", e.target.value)}
                  />
                  <input
                    style={{ ...S.inp(d.errors.minPayment), padding: "7px 10px" }}
                    placeholder={u.placeholderMin}
                    type="number"
                    value={d.minPayment}
                    onChange={e => updateDebt(d.id, "minPayment", e.target.value)}
                  />
                  <button onClick={() => { isDemoRef.current = false; setDebts(ds => ds.filter(x => x.id !== d.id)); }} style={{ background: "transparent", color: C.textMuted, border: "none", fontSize: "18px", cursor: "pointer", padding: 0, lineHeight: 1, textAlign: "center" }}>x</button>
                </div>
                {/* Error messages below row if any */}
                {hasErr && (
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 90px 28px", gap: "6px", marginTop: "3px" }}>
                    <div style={S.err}>{d.errors.type || ""}</div>
                    <div style={S.err}>{d.errors.balance || ""}</div>
                    <div style={S.err}>{d.errors.rate || ""}</div>
                    <div style={S.err}>{d.errors.minPayment || ""}</div>
                    <div></div>
                  </div>
                )}
              </div>
            );
          })}
          <button className="btn-h" style={S.btnG} onClick={() => { isDemoRef.current = false; setDebts(ds => [...ds, emptyDebt()]); }}>{u.addDebt}</button>
        </div>

        <div style={S.card}>
          <div style={S.sT}>{u.extraTitle}</div>
          <div style={{ display: "flex", gap: "10px" }}>
            {[
              { label: u.conservative, idx: 0 },
              { label: u.balanced, idx: 1 },
              { label: u.aggressive, idx: 2 },
            ].map(({ label, idx }) => (
              <div key={idx} style={{ flex: 1 }}>
                <div style={{ fontSize: "10px", color: C.textMuted, fontWeight: 600, marginBottom: "6px", letterSpacing: "0.5px" }}>{label}</div>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: C.textMuted, fontSize: "13px" }}>{localeCfg.symbol}</span>
                  <input
                    style={{ ...S.inp(false), paddingLeft: `${Math.max(24, 8 + localeCfg.symbol.length * 9)}px` }}
                    type="number"
                    value={extraScenarios[idx]}
                    onChange={e => setExtraScenarios(prev => { const next = [...prev]; next[idx] = e.target.value; return next; })}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "10px", fontSize: "13px", color: C.accent }}>{typeof u.extraHint === 'function' ? u.extraHint(localeCfg.symbol) : u.extraHint}</div>
        </div>

        {totalDebt > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 4px", marginBottom: "10px" }}>
            <span style={{ color: C.textMuted }}>{u.totalDebt}</span>
            <span style={{ fontWeight: 700, fontSize: "19px" }}>{fmtL(totalDebt)}</span>
          </div>
        )}

        <button className="btn-h" style={S.btnP} onClick={calculate}>
          {u.calculateBtn}
        </button>
        <div style={{ textAlign: "center", marginTop: "10px", fontSize: "12px", color: C.textMuted }}>{u.freeNoSignup}</div>
      </div>

      {beforeFooter}
      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: "40px 24px 32px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ fontSize: "11px", letterSpacing: "3px", color: C.accent, fontWeight: 700, marginBottom: "28px", textAlign: "center" }}>ClearMyDebt</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: "28px 20px", marginBottom: "32px" }}>

            {/* Calculators column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.calculators}</div>
              {[
                { l: u.nav.debtCalculator, h: localePath(locale, '/') },
                { l: u.nav.creditCard,     h: localePath(locale, '/credit-card-payoff-calculator/') },
                { l: u.nav.snowball,       h: localePath(locale, '/debt-snowball-calculator/') },
                { l: u.nav.avalanche,      h: localePath(locale, '/debt-avalanche-calculator/') },
                { l: u.nav.repayment,      h: localePath(locale, '/debt-repayment-calculator/') },
                { l: u.nav.studentLoan,    h: localePath(locale, '/student-loan-payoff-calculator/') },
                { l: u.nav.personalLoan,   h: localePath(locale, '/personal-loan-payoff-calculator/') },
                { l: u.nav.carLoan,        h: localePath(locale, '/car-loan-payoff-calculator/') },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px", lineHeight: 1.4 }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

            {/* Resources column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.resources}</div>
              {[
                { l: u.nav.about,    h: localePath(locale, '/about') },
                { l: u.nav.faq,      h: localePath(locale, '/faq') },
                { l: u.nav.contact,  h: localePath(locale, '/contact') },
                { l: u.nav.blog,     h: '/blog/' },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px" }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

            {/* Legal column */}
            <div>
              <div style={{ fontSize: "10px", letterSpacing: "2px", color: C.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: "12px" }}>{u.nav.legal}</div>
              {[
                { l: u.nav.tos,     h: localePath(locale, '/tos') },
                { l: u.nav.privacy, h: localePath(locale, '/privacy') },
              ].map(lnk => (
                <a key={lnk.h} href={lnk.h} style={{ display: "block", fontSize: "13px", color: C.textMuted, textDecoration: "none", marginBottom: "7px" }}
                  onMouseOver={e => e.target.style.color = C.accent}
                  onMouseOut={e => e.target.style.color = C.textMuted}
                >{lnk.l}</a>
              ))}
            </div>

          </div>
          <div style={{ fontSize: "12px", color: C.textMuted, textAlign: "center", paddingTop: "20px", borderTop: `1px solid ${C.border}` }}>{u.copyright}</div>
        </div>
      </div>
    </div>
  );
}
